// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * Appearance preferences follow the person to every device (`/v1/me/preferences`): theme,
 * editor font and size, readable width, live preview, and line numbers. When signed in, the server's copy
 * wins at startup, at sign-in, when the window comes back into view, and every half minute while
 * it is in view (so a switch on one device reaches the others soon); a change here is sent
 * half a second after the last one. localStorage stays the cache, so the app paints at once.
 * Vim, hotkeys, and the formatting bar stay on this device; signed out, everything does.
 */
import type { components } from "@granite/api-client";
import type { StoreApi } from "zustand/vanilla";

import type { Preferences } from "./preferences.js";
import type { SessionState } from "./session.js";

type Remote = components["schemas"]["Preferences"];

/** The preferences that sync; the rest stay on this device. */
export const syncedKeys = ["theme", "editorFont", "fontSize", "readableWidth", "livePreview", "lineNumbers"] as const;

type Synced = Pick<Preferences, (typeof syncedKeys)[number]>;

export function toRemote(p: Preferences): Remote {
  return {
    theme: p.theme,
    editor_font: p.editorFont,
    font_size: p.fontSize,
    readable_width: p.readableWidth,
    live_preview: p.livePreview,
    line_numbers: p.lineNumbers,
  };
}

export function fromRemote(r: Remote): Synced {
  return {
    theme: r.theme,
    editorFont: r.editor_font,
    fontSize: r.font_size,
    readableWidth: r.readable_width,
    livePreview: r.live_preview,
    lineNumbers: r.line_numbers,
  };
}

function sameSynced(a: Preferences, b: Preferences): boolean {
  return syncedKeys.every((k) => a[k] === b[k]);
}

export interface SyncDeps {
  preferences: StoreApi<Preferences>;
  session: StoreApi<SessionState>;
  load: () => Promise<Remote>;
  save: (r: Remote) => Promise<Remote>;
  /** Milliseconds to wait after the last change before sending (500). */
  delay?: number;
}

/** Starts syncing; returns a function that stops it. */
export function syncPreferences(deps: SyncDeps): () => void {
  const { preferences, session, load, save, delay = 500 } = deps;
  let applyingRemote = false;
  // Changes made here, and how many of them the server has stored (fewer while offline).
  let edits = 0;
  let saved = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const signedIn = () => session.getState().status === "signedIn";

  const push = async () => {
    timer = undefined;
    if (!signedIn()) return;
    const sending = edits;
    try {
      await save(toRemote(preferences.getState()));
      saved = Math.max(saved, sending);
    } catch {
      // Offline or refused: sent again at the next sign-in or when the window is back.
    }
  };

  const schedule = () => {
    edits++;
    clearTimeout(timer);
    timer = setTimeout(() => void push(), delay);
  };

  const pull = async () => {
    if (!signedIn()) return;
    if (saved < edits) {
      clearTimeout(timer);
      await push();
      return;
    }
    const before = edits;
    let remote: Remote;
    try {
      remote = await load();
    } catch {
      return; // offline: the cached copy stays
    }
    // Changed here while the request was out: that change is newer.
    if (edits !== before) return;
    if (!remote.updated_at) {
      // Never saved on the server: this device's settings become everyone's.
      const local = preferences.getState();
      if (!sameSynced(local, { ...local, ...fromRemote(remote) })) await push();
      return;
    }
    applyingRemote = true;
    try {
      preferences.setState(fromRemote(remote));
    } finally {
      applyingRemote = false;
    }
  };

  const unsubPrefs = preferences.subscribe((next, prev) => {
    if (applyingRemote || sameSynced(next, prev) || !signedIn()) return;
    schedule();
  });

  let lastStatus = session.getState().status;
  const unsubSession = session.subscribe((s) => {
    if (s.status === lastStatus) return;
    lastStatus = s.status;
    if (s.status === "signedIn") void pull();
  });

  const onVisible = () => {
    if (document.visibilityState === "visible") void pull();
  };
  document.addEventListener("visibilitychange", onVisible);
  const interval = setInterval(() => {
    if (document.visibilityState === "visible" && signedIn()) void pull();
  }, 30_000);

  void pull();

  return () => {
    clearTimeout(timer);
    unsubPrefs();
    unsubSession();
    document.removeEventListener("visibilitychange", onVisible);
    clearInterval(interval);
  };
}
